// Theme management utility functions

/**
 * Checks if dark mode is currently enabled
 * @returns {boolean} True if dark mode is enabled
 */
export const isDarkMode = () => {
  // Check for saved preference in localStorage
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme) {
    return savedTheme === 'dark';
  }
  
  // If no saved preference, check for system preference
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * Set dark mode on or off
 * @param {boolean} isDark Whether to enable dark mode
 */
export const setDarkMode = (isDark) => {
  // Save the preference
  localStorage.setItem('theme', isDark ? 'dark' : 'light');
  
  // Apply the theme to the body element
  if (isDark) {
    document.body.classList.add('dark-theme');
  } else {
    document.body.classList.remove('dark-theme');
  }
  
  // Optional: Dispatch an event so other components can react to the theme change
  window.dispatchEvent(new CustomEvent('themechange', { 
    detail: { isDark } 
  }));

  // Send message to Flutter
  if (window.flutter_inappwebview) {
    window.flutter_inappwebview.callHandler('updateTheme', { isDark });
  }
};

/**
 * Toggle dark mode on/off
 * @returns {boolean} The new dark mode state (true if dark mode is now on)
 */
export const toggleDarkMode = () => {
  const newDarkMode = !isDarkMode();
  setDarkMode(newDarkMode);
  return newDarkMode;
};

/**
 * Initialize theme based on saved preferences or system preference
 * Call this on app initialization to set the correct theme
 */
export const initTheme = () => {
  setDarkMode(isDarkMode());
  
  // Listen for Flutter Bridge ready event to sync initial state
  // This handles the race condition where the app loads before the native bridge is ready
  window.addEventListener('flutterInAppWebViewPlatformReady', (event) => {
    // Re-send current theme to ensure native side is in sync
    const isDark = isDarkMode();
    if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('updateTheme', { isDark });
    }
  });
  
  // Optional: Listen for system preference changes
  if (window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', event => {
        // Only apply if user hasn't set a preference
        if (!localStorage.getItem('theme')) {
          setDarkMode(event.matches);
        }
      });
  }
};